/**
 * buildData — turn a raw grid (nodes, edges, load curve, horizon) into the
 * interpreter's *base* input relations. Together with the rules this is what
 * builds the LP: it produces the entity tables, the computed `demand`, and the
 * per-unit renewable availability. Neither the decision variables nor the
 * derived relations are built here — grounding enumerates those from the rules.
 *
 * `demand` is the one piece with real logic: the per-hour total load (from the
 * load curve) split across the load buses in proportion to their size.
 */

import { GridNode, GridEdgeTopology } from '../../types';
import { BezierCurve, evaluateBezierCurve } from '../../bezier/bezier';
import { Data, Row } from './interpret';

export interface BuildDataParams {
  nodes: GridNode[];
  edges: GridEdgeTopology[];
  loadCurve: BezierCurve;
  /** Wind/solar availability curves; used to cap those generators per hour. */
  windCurve?: BezierCurve;
  solarCurve?: BezierCurve;
  numHours: number;
  /** Shifts where the curves are sampled: period t samples at t + hourOffset. */
  hourOffset?: number;
  /** Overrides a bus's demand at one hour (the LMP-curve sweep), replacing the split. */
  overrideNodalDemand?: { hour: number; demands: Map<string, number> };
}

const seq = (n: number): number[] => Array.from({ length: n }, (_, i) => i);

/** A load's own demand magnitude (MW), from its rated power or dispatch value. */
const demandOf = (l: GridNode): number =>
  l.maxPowerMW ? Math.abs(l.maxPowerMW) : l.value ? Math.abs(l.value) : 0;

/**
 * The MW a node withdraws at an hour whose total load is `totalLoadMW`. A load
 * bus takes its proportional share; everything else withdraws 0. Mirrors
 * `addNodalBalanceConstraintsForTime`, including its defaults.
 */
function nodalDemand(node: GridNode, loads: GridNode[], totalLoadMW: number): number {
  if (node.type !== 'load') return 0;
  const loadDemand = demandOf(node);
  if (loadDemand > 0) {
    const totalDemand = loads.reduce((sum, l) => sum + demandOf(l), 0);
    return totalDemand > 0 ? totalLoadMW * (loadDemand / totalDemand) : 10;
  }
  if (loads.length > 0 && totalLoadMW > 0) {
    const withoutDemand = loads.filter((l) => demandOf(l) === 0).length;
    return withoutDemand > 0 ? totalLoadMW / withoutDemand : totalLoadMW / loads.length;
  }
  return 10;
}

export function buildData(params: BuildDataParams): Data {
  const { nodes, edges, loadCurve, windCurve, solarCurve, numHours, hourOffset = 0, overrideNodalDemand } = params;
  const slackId = nodes[0]?.id; // the solver treats componentNodes[0] as slack
  const generators = nodes.filter((n) => n.type === 'generator');
  const batteries = nodes.filter((n) => n.type === 'battery');
  const loads = nodes.filter((n) => n.type === 'load');
  const windCount = generators.filter((g) => g.fuelType === 'wind').length;
  const solarCount = generators.filter((g) => g.fuelType === 'solar').length;

  // Which buses get a nodal-balance row: skip an isolated generator (it can't
  // export, so its balance would force it to 0 and clash with its min-power
  // bound), and skip a bus that would contribute no terms at all (no
  // generator/battery/incident line).
  const incident = new Set<string>();
  edges.forEach((e) => {
    incident.add(e.from);
    incident.add(e.to);
  });
  const inBalance = (n: GridNode): 0 | 1 => {
    const hasLine = incident.has(n.id);
    const isGen = n.type === 'generator';
    const isolatedGen = !hasLine && isGen && n.type !== 'load';
    const hasCoeffs = isGen || n.type === 'battery' || hasLine;
    return !isolatedGen && hasCoeffs ? 1 : 0;
  };

  const demand: Row[] = [];
  const windStrength: Row[] = [];
  const solarStrength: Row[] = [];
  for (const t of seq(numHours)) {
    const totalLoadMW = evaluateBezierCurve(loadCurve, t + hourOffset);
    const override = overrideNodalDemand?.hour === t ? overrideNodalDemand.demands : undefined;
    for (const n of nodes) {
      const value = override?.has(n.id) ? override.get(n.id)! : nodalDemand(n, loads, totalLoadMW);
      demand.push({ bus: n.id, timestep: t, value });
    }

    // Availability per *unit* of each fuel: the curve for this hour split evenly
    // across the units burning it. The per-generator cap is no longer computed
    // here — the `gen_cap` rules derive it from these and the generator's own
    // maximum, so it can be read and edited like any other rule.
    windStrength.push({
      timestep: t,
      value: windCount > 0 && windCurve ? evaluateBezierCurve(windCurve, t + hourOffset) / windCount : 0,
    });
    solarStrength.push({
      timestep: t,
      value: solarCount > 0 && solarCurve ? evaluateBezierCurve(solarCurve, t + hourOffset) / solarCount : 0,
    });
  }

  // Defaults use `||` (falsy), not `??` — a 0 cost becomes 1, a 0 max becomes
  // 999999, etc. These fallbacks are load-bearing: the objective and bounds
  // rules assume every generator has a positive cost and cap.
  return {
    horizon: [{ t: numHours }], // the single final boundary
    wind_strength: windStrength,
    solar_strength: solarStrength,
    bus: nodes.map((n) => ({ id: n.id, slack: n.id === slackId ? 1 : 0, in_balance: inBalance(n) })),
    timestep: seq(numHours).map((t) => ({ t })),
    soc_time: seq(numHours + 1).map((t) => ({ t })), // period boundaries 0..N
    gen: generators.map((g) => ({
      id: g.id,
      min: g.minPowerMW || 0,
      max: g.maxPowerMW || 999999,
      cost: g.costPerMWh || 1,
      fuel: g.fuelType || 'fossil',
    })),
    line: edges.map((e) => ({ id: e.id, from: e.from, to: e.to, reactance: e.reactance || 0.1, rating: e.rating || 999999 })),
    battery: batteries.map((b) => ({
      id: b.id,
      efficiency: b.efficiency || 0.9,
      max_charge: b.maxChargeMW || 100,
      max_discharge: b.maxDischargeMW || 100,
      min_energy: b.minEnergyMWh || 0,
      max_energy: b.maxEnergyMWh || 400,
      initial_soc: b.initialEnergyMWh || (b.maxEnergyMWh || 400) / 2,
    })),
    demand,
  };
}
